import { useNavigate, useOutletContext, useParams } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { Authorized } from 'modules/core/components/Authorized'
import LabeledInput from 'modules/core/components/LabeledInput'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { usePortal } from 'modules/core/lib/portalContext'
import app from 'modules/core/store/models/app'

import DropItem, { UploadFile } from 'modules/expenses/components/common/DropItem'
import DropZoneBox from 'modules/expenses/components/common/DropZone'
import SubmitOcrAnimation from 'modules/expenses/components/common/SubmitOcrAnimation'
import {
  buildErrorMessages,
  getOcrFriendlyErrorMessage,
} from 'modules/expenses/components/expenses/Form/Buttons/helpers/ActionErrorMessageHandler'
import { useCreateFromDocument } from 'modules/expenses/hooks/useExpensable'
import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import type { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import {
  AmplitudeExpenseType,
  trackAddExpensable,
  trackCreatedExpensable,
  trackSaveExpensable,
  trackUploadDocument,
} from 'modules/expenses/lib/expensables/expensableTracking'
import ModularizableForm from 'modules/expenses/lib/forms/modularizableForm'

interface ExpenseOcrModalProps {
  signedDocumentsTerms: number
  onClose: () => void
}

interface FilesDropZoneProps {
  mandatory: boolean
  onDropFiles: (filesOnDrop: File[]) => void
}

interface FileListProps {
  files: File[]
  onRemoveUploadFile: (file: UploadFile) => void
}

const FilesDropZone = ({ mandatory, onDropFiles }: FilesDropZoneProps) => {
  const {
    company: { subscription },
  } = useCurrentSession()

  return (
    <Authorized subscription={subscription}>
      <LabeledInput mandatory={mandatory} isLegend>
        <DropZoneBox
          onDrop={onDropFiles}
          title={i18n.t('expenses_v2.forms.fields.dropzone.title')}
          description={i18n.t('expenses_v2.forms.fields.dropzone.description')}
          acceptedFiles={{
            'image/*': [],
            'application/pdf': [],
            'application/xml': [],
            'text/xml': [],
          }}
          multiple
        />
      </LabeledInput>
    </Authorized>
  )
}

const FileList = ({ files, onRemoveUploadFile }: FileListProps) => (
  <Box flexDirection="column" justifyContent="center" width="full" gap="s16">
    {files.map((file) => (
      <DropItem
        key={file.name}
        file={{
          id: null,
          name: file.name,
          type: file.type,
          size: String(file.size),
          preview: URL.createObjectURL(file),
        }}
        showInViewer={false}
        downloadable={false}
        onRemove={(file: UploadFile) => onRemoveUploadFile(file)}
        removable
      />
    ))}
  </Box>
)

const ExpenseOcrModal = ({ signedDocumentsTerms, onClose }: ExpenseOcrModalProps) => {
  const { taxAgencyOrganizationId } = useOutletContext<RouteContextParams>()
  const {
    company: { id: companyId },
    employee,
  } = useCurrentSession()
  const { closePortal } = usePortal()
  const { navigation } = useExpensesNavigation()
  const { groupId } = useParams()
  const navigate = useNavigate()

  const { createFromDocument, loading } = useCreateFromDocument()

  const form = useLazyRef(() => new ModularizableForm({}, { files: 'any' }, ['files'])).current
  const files = form.get('files').value

  const showFileDropZone = !loading && files.length === 0
  const showFileList = !loading && files.length > 0
  const hasFiles = !form.isMandatory('files') || files.length > 0

  const onDropFiles = (filesOnDrop: File[]) => {
    const oldFiles = form.get('files').value || []
    const newFiles = oldFiles.concat(filesOnDrop)
    form.get('files').set(newFiles)
  }

  const onRemoveUploadFile = (removedFile: UploadFile) => {
    const filteredFiles = form
      .get('files')
      .value.filter(
        (file: UploadFile) => file.name !== removedFile.name && file.size !== removedFile.size
      )

    form.get('files').set(filteredFiles)
  }

  const onSubmit = async () => {
    if (!employee) return
    trackUploadDocument('next')

    const { files = [] } = form.data()
    const result = await createFromDocument({
      files,
      groupId: groupId ? Number(groupId) : null,
      companyId: companyId,
      employeeId: employee.id,
      signedForOrganization: taxAgencyOrganizationId,
      documentOrigin: 'file',
    })

    const handled = handleResult(result, (errors) => {
      form.setErrors(errors)
      const errorMessages = buildErrorMessages(errors)
      const displayMessage = getOcrFriendlyErrorMessage(
        errorMessages || i18n.t('errors.generic_error')
      )
      app.setFlash({
        type: 'wrong',
        message: displayMessage,
        time: 3000,
      })
    })

    if (!handled.success) {
      trackSaveExpensable({
        expense_type: AmplitudeExpenseType.General,
        ocr: true,
        action: 'new',
        success: false,
        context: 'personal',
      })
      return handled
    }

    const expensable = handled.data?.expensable
    if (!expensable) return handled

    trackSaveExpensable({
      expense_id: expensable.id,
      expense_type: AmplitudeExpenseType.General,
      creation_type: 'manual',
      ocr: true,
      current_status: expensable.status,
      action: 'new',
      success: true,
      context: 'personal',
    })
    trackCreatedExpensable({
      id: expensable.id,
      type: expensable.type,
      creationType: expensable.expense?.creationType,
      category: expensable.expense?.category?.id,
      status: expensable.status,
      fromDocument: true,
    })
    navigate({
      pathname: navigation.helpers.expensableDetail(expensable.id),
    })
    closePortal()
  }

  const ocrDescription = !taxAgencyOrganizationId
    ? i18n.t('expenses_v2.modals.new_expense_ocr.description_enabled')
    : i18n.t('expenses_v2.modals.new_expense_ocr.description_enabled_desktop')

  useEffect(() => {
    trackAddExpensable(AmplitudeExpenseType.General)
  }, [])

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      locked={loading}
      illustration="document-folder-disorder-list"
      title={!loading ? i18n.t('expenses_v2.modals.new_expense_ocr.title') : ''}
      description={
        !loading && (
          <>
            <Box paddingBottom="s32">
              {i18n.t('expenses_v2.modals.new_expense_ocr.description')}
            </Box>
            {signedDocumentsTerms > 0 ? (
              <Box>{ocrDescription}</Box>
            ) : (
              <Box>{i18n.t('expenses_v2.modals.new_expense_ocr.description_disabled')}</Box>
            )}
          </>
        )
      }
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        !loading && (
          <>
            <Button.Router
              to={navigation.helpers.newExpense()}
              type="secondary"
              disabled={loading || !employee}
              onClick={() => trackUploadDocument('skip')}
            >
              {i18n.t('expenses_v2.buttons.skip_step')}
            </Button.Router>
            <Button disabled={loading || !employee || !hasFiles} type="primary" submit>
              {i18n.t('expenses_v2.buttons.next')}
            </Button>
          </>
        )
      }
    >
      <Box
        flexDirection="column"
        justifyContent="center"
        alignItems="flexStart"
        width="full"
        gap="s6"
      >
        {loading && <SubmitOcrAnimation operation="create" />}
        {showFileDropZone && <FilesDropZone mandatory={!hasFiles} onDropFiles={onDropFiles} />}
        {showFileList && (
          <FileList files={form.get('files').value} onRemoveUploadFile={onRemoveUploadFile} />
        )}
      </Box>
    </Modal>
  )
}

export default observer(ExpenseOcrModal)
